import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import './AdDetailsQA.css';

const AdDetailsQA = ({ adId, ownerId }) => {
    const [sessionUser, setSessionUser] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newQuestion, setNewQuestion] = useState('');
    const [answerTexts, setAnswerTexts] = useState({});
    const [showTooltip, setShowTooltip] = useState(false);

    // Fetch session user
    useEffect(() => {
        fetch(`${process.env.REACT_APP_API_URL}/auth/session-user`, {
            credentials: 'include'
        })
            .then(r => r.ok ? r.json() : Promise.reject())
            .then(data => setSessionUser(data.user))
            .catch(() => setSessionUser(null));
    }, []);

    // Fetch questions
    const loadQuestions = () => {
        fetch(`${process.env.REACT_APP_API_URL}/listings/${adId}/questions`)
            .then(r => r.json())
            .then(setQuestions)
            .finally(() => setLoading(false));
    };
    useEffect(loadQuestions, [adId]);

    const hasAsked = sessionUser
        ? questions.some(q => q.questioner.id === sessionUser.id)
        : false;
    const isOwner = sessionUser?.id === ownerId;

    const handleAsk = e => {
        e.preventDefault();
        fetch(`${process.env.REACT_APP_API_URL}/listings/${adId}/questions`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ questionText: newQuestion })
        })
            .then(r => r.ok ? r.json() : r.json().then(x => Promise.reject(x)))
            .then(() => {
                setNewQuestion('');
                loadQuestions();
            })
            .catch(err => alert(err.error || 'Could not post question'));
    };

    const handleAnswer = (questionId) => {
        const text = answerTexts[questionId];
        if (!text) return alert('Please enter an answer');
        fetch(`${process.env.REACT_APP_API_URL}/questions/${questionId}/answer`, {
            method: 'PUT',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ answerText: text })
        })
            .then(r => r.ok ? r.json() : r.json().then(x => Promise.reject(x)))
            .then(() => {
                setAnswerTexts(prev => ({ ...prev, [questionId]: '' }));
                loadQuestions();
            })
            .catch(err => alert(err.error || 'Could not submit answer'));
    };

    const handleDelete = (questionId) => {
        Swal.fire({
            title: 'Ste prepričani?',
            text: 'Vprašanje bo trajno izbrisano!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#FF4367',
            cancelButtonColor: '#6D7588',
            confirmButtonText: 'Da, izbriši!',
            cancelButtonText: 'Prekliči'
        }).then((result) => {
            if (result.isConfirmed) {
                fetch(`${process.env.REACT_APP_API_URL}/questions/${questionId}`, {
                    method: 'DELETE',
                    credentials: 'include',
                })
                    .then((res) => {
                        if (res.status === 204) {
                            loadQuestions();
                            Swal.fire('Izbrisano!', 'Vprašanje je bilo izbrisano.', 'success');
                        } else {
                            throw new Error('Ne morem izbrisati vprašanja.');
                        }
                    })
                    .catch((err) => {
                        Swal.fire('Napaka!', err.message, 'error');
                    });
            }
        });
    };

    if (loading) return <p>Loading Q/A…</p>;

    return (
        <div className="qa_container">
            <h5 className="qa_title">
                Vprašanja in odgovori
                <span className="qa_info_wrapper" onClick={() => setShowTooltip(prev => !prev)}>
                    ⓘ
                    {showTooltip && (
                        <span className="qa_tooltip">Pod posameznim oglasom lahko zastavite 1 vprašanje.</span>
                    )}
                </span>
            </h5>

            {/* Ask form */}
            {sessionUser && !isOwner && !hasAsked && (
                <form className="qa_ask_form" onSubmit={handleAsk}>
                    <textarea
                        className="qa_input"
                        placeholder="Postavite vprašanje prodajalcu…"
                        value={newQuestion}
                        onChange={e => setNewQuestion(e.target.value)}
                        required
                    />
                    <button type="submit" className="qa_button">Pošlji vprašanje</button>
                </form>
            )}

            {/* Login prompt */}
            {!sessionUser && (
                <p className="qa_login_prompt">
                    <a href="/prijava">Prijavite se</a> za postavljanje vprašanj.
                </p>
            )}

            {/* Questions list */}
            <ul className="qa_list">
                {questions.map(q => (
                    <li key={q.id} className="qa_item">
                        <div className="qa_question">
                            <span><strong>{q.questioner.username}:</strong> {q.question_text}</span>
                            {(sessionUser?.id === q.questioner.id || sessionUser?.id === ownerId) && (
                                <button
                                    className="qa_delete_btn"
                                    onClick={() => handleDelete(q.id)}
                                    title="Izbriši vprašanje"
                                >
                                    🗑️
                                </button>
                            )}
                        </div>

                        {/* If answered */}
                        {q.answer_text ? (
                            <div className="qa_answer">
                                <strong>Odgovor:</strong> {q.answer_text}
                            </div>
                        ) : (
                            // Owner’s answer form
                            isOwner && (
                                <div className="qa_answer_form">
                                    <textarea
                                        className="qa_input"
                                        placeholder="Vaš odgovor…"
                                        value={answerTexts[q.id] || ''}
                                        onChange={e =>
                                            setAnswerTexts(prev => ({ ...prev, [q.id]: e.target.value }))
                                        }
                                    />
                                    <button
                                        onClick={() => handleAnswer(q.id)}
                                        className="qa_button"
                                    >Odgovori</button>
                                </div>
                            )
                        )}
                    </li>
                ))}
            </ul>
        </div>
    );
};

export default AdDetailsQA;